import { Chip } from "@mui/material";
import { format, isToday } from "date-fns";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { eventClick } from "components/helper";
import { entryDurationMinutes, formatDuration } from "utils/timesheetUtils";

const formatClockIn = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return isToday(date) ? format(date, "hh:mm a") : format(date, "dd MMM yyyy hh:mm a");
};

const TimesheetWorkingTodayTable = ({ rows = [], isLoading }) => {
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        accessorKey: "employeeName",
        header: "Employee",
        size: 180,
        Cell: ({ renderedCellValue }) => (
          <span className="font-semibold text-app-text-secondary">{renderedCellValue || "-"}</span>
        ),
      },
      {
        accessorKey: "department",
        header: "Department",
        size: 140,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        accessorKey: "jobTitle",
        header: "Job title",
        size: 160,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        id: "clockIn",
        header: "Clocked in",
        size: 160,
        accessorFn: (row) => row.clockIn || row.clockInAt,
        Cell: ({ row }) => formatClockIn(row.original.clockIn || row.original.clockInAt),
      },
      {
        id: "worked",
        header: "Time so far",
        size: 120,
        accessorFn: (row) => entryDurationMinutes(row),
        Cell: ({ row }) => formatDuration(entryDurationMinutes(row.original)),
      },
      {
        id: "status",
        header: "Status",
        size: 130,
        Cell: () => <Chip size="small" color="success" label="Clocked in" />,
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    enableRowNumbers: true,
    rowNumberDisplayMode: "original",
    enableStickyHeader: true,
    enablePagination: true,
    initialState: {
      showGlobalFilter: true,
      density: "compact",
      sorting: [{ id: "clockIn", desc: false }],
      pagination: { pageIndex: 0, pageSize: 20 },
    },
    muiTableContainerProps: {
      sx: { maxHeight: "60vh", minHeight: "40vh", overflowY: "auto" },
    },
    state: {
      isLoading,
      showProgressBars: isLoading,
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        const employeeId = row.original.employeeId || row.original.employee?.id;
        if (!eventClick(event) && employeeId) {
          navigate(`/admin/timesheets/view/${employeeId}`);
        }
      },
      sx: {
        cursor: "pointer",
        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
      },
    }),
  });

  return <MaterialReactTable table={table} />;
};

export default TimesheetWorkingTodayTable;
